import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Inbox, Wallet } from 'lucide-react';
import {
  mainOeuvreApi, reduirePhoto, LIBELLE_MODE,
  type FeuilleSemaine, type ModePaiement,
} from '@/api/mainOeuvre';
import { toast } from '@/stores/toastStore';
import { formatFCFA } from '@/utils/currency';

/**
 * PDF joint à un règlement (10/10/2026) : envoyé tel quel, donc borné à 3 Mo — la limite d'une pièce côté serveur
 * (PieceUploadService.MAX_OCTETS). L'adresse du serveur accepte 6 Mo par envoi depuis le 10/10 (accord PDG), ce qui
 * couvre l'alourdissement d'un tiers dû à l'encodage. Une photo, elle, est réduite avant l'envoi.
 */
const PDF_MAX_OCTETS = 3 * 1024 * 1024;

function lireEnBase64(fichier: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(String(lecteur.result));
    lecteur.onerror = () => reject(lecteur.error ?? new Error('Lecture du fichier impossible'));
    lecteur.readAsDataURL(fichier);
  });
}

/**
 * Le point de la semaine, côté bureau — 30/09/2026.
 *
 * « En fin de semaine on a le point global de l'équipe : la quantité produite
 * par type et le montant total à payer. » Une carte par équipe (le chef de
 * site les saisit toutes pour l'instant — précision PDG du 30/09). Le DAF
 * valide la semaine en une fois ; la caisse paie ensuite, une écriture et une
 * liste émargée pour toute l'équipe.
 */

const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function lundiDe(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
}
function decaler(iso: string, jours: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + jours);
  return d.toISOString().slice(0, 10);
}
const dateLongue = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'UTC' });

export function OngletSemaines({ estHolding }: { estHolding: boolean }) {
  const [lundi, setLundi]       = useState(() => lundiDe(new Date()));
  const [aPayer, setAPayer]     = useState<FeuilleSemaine | null>(null);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['main-oeuvre', 'semaine', lundi],
    queryFn:  () => mainOeuvreApi.semaine(lundi),
    staleTime: 20_000,
  });

  const rafraichir = () => qc.invalidateQueries({ queryKey: ['main-oeuvre'] });
  const erreurDe = (e: any) => e?.response?.data?.error?.message ?? 'Opération impossible.';

  const valider = useMutation({
    mutationFn: (id: string) => mainOeuvreApi.validerSemaine(id),
    onSuccess:  () => { toast.success('Semaine validée', 'Elle peut maintenant être payée.'); rafraichir(); },
    onError:    (e) => toast.error('Validation refusée', erreurDe(e)),
  });
  const rouvrir = useMutation({
    mutationFn: (id: string) => mainOeuvreApi.rouvrirSemaine(id),
    onSuccess:  () => { toast.success('Validation annulée', 'Le chef peut de nouveau corriger ses journées.'); rafraichir(); },
    onError:    (e) => toast.error('Impossible', erreurDe(e)),
  });

  const data = q.data;
  const courante = lundi === lundiDe(new Date());

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setLundi(decaler(lundi, -7))} aria-label="Semaine précédente">
          <ChevronLeft size={14} />
        </button>
        <strong style={{ fontSize: 14 }}>
          Semaine du {dateLongue(lundi)} au {dateLongue(decaler(lundi, 6))}
        </strong>
        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setLundi(decaler(lundi, 7))} aria-label="Semaine suivante" disabled={courante}>
          <ChevronRight size={14} />
        </button>
        {!courante && (
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setLundi(lundiDe(new Date()))}>Cette semaine</button>
        )}
      </div>

      {data && data.feuilles.length > 0 && (
        <div className="ds-kpi-strip" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14, fontSize: 13 }}>
          <span>Équipes : <strong>{data.resume.nbFeuilles}</strong></span>
          <span>À valider : <strong>{data.resume.aValider}</strong></span>
          <span>Total de la semaine : <strong>{formatFCFA(data.resume.totalCents)}</strong></span>
          <span>Payé : <strong>{formatFCFA(data.resume.regleCents)}</strong></span>
          <span>Reste à payer : <strong style={{ color: BigInt(data.resume.resteCents) > 0n ? 'var(--warning)' : undefined }}>
            {formatFCFA(data.resume.resteCents)}
          </strong></span>
        </div>
      )}

      {q.isLoading && <p style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</p>}
      {q.isError && <p style={{ padding: 24, textAlign: 'center', color: 'var(--danger)' }}>{erreurDe(q.error)}</p>}
      {data && data.feuilles.length === 0 && (
        <div className="ds-table-panel" style={{ padding: 40, textAlign: 'center' }}>
          <Inbox size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
          <p style={{ fontSize: 13, color: 'var(--tx-3)', maxWidth: 520, margin: '0 auto' }}>
            {estHolding
              ? 'SIM GROUP ne fabrique pas : les journées de chantier appartiennent aux filiales. Ouvrez Leadbeton CI depuis Holding, en « Vue PDG ».'
              : 'Aucune journée de chantier cette semaine. Les chefs d\'équipe ouvrent leur journée le matin depuis le portail terrain, et la clôturent le soir.'}
          </p>
        </div>
      )}

      {data?.feuilles.map((f) => (
        <CarteFeuille
          key={f.feuilleId} f={f} lundi={lundi}
          onValider={() => valider.mutate(f.feuilleId)} validation={valider.isPending}
          onRouvrir={() => rouvrir.mutate(f.feuilleId)}
          onPayer={() => setAPayer(f)}
        />
      ))}

      {aPayer && <ModalePaie f={aPayer} onFermer={() => setAPayer(null)} onPaye={() => { setAPayer(null); rafraichir(); }} />}
    </>
  );
}

function CarteFeuille({
  f, lundi, onValider, validation, onRouvrir, onPayer,
}: {
  f: FeuilleSemaine; lundi: string; onValider: () => void; validation: boolean; onRouvrir: () => void; onPayer: () => void;
}) {
  const reste = BigInt(f.resteCents);
  const regle = BigInt(f.regleCents);
  const statut =
    f.statut === 'en_cours' ? { lib: 'En cours', cls: 'ds-bd-n' }
    : reste === 0n ? { lib: 'Payée', cls: 'ds-bd-s' }
    : regle > 0n ? { lib: 'Payée en partie', cls: 'ds-bd-w' }
    : { lib: 'Validée — à payer', cls: 'ds-bd-p' };
  const parJour = new Map(f.journees.map((j) => [j.dateActivite, j]));

  return (
    <div className="ds-table-panel" style={{ marginBottom: 16 }}>
      <div className="ds-table-header" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="ds-table-title">
            {f.equipes.length ? f.equipes.map((e) => e.nom).join(' + ') : 'Journaliers'}{' '}
            <span className={`ds-bd ${statut.cls}`} style={{ fontSize: 10, marginLeft: 6 }}>{statut.lib}</span>
          </div>
          <div className="ds-table-sub">Saisi par : {f.chefs.map((c) => c.nom).join(', ')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>Total de la semaine</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{formatFCFA(f.totalCents)}</div>
          </div>
          {f.statut === 'en_cours' && (
            <button
              className="ds-btn ds-btn-p ds-btn-sm" onClick={onValider}
              disabled={validation || f.nbJourneesOuvertes > 0 || f.journees.length === 0}
              title={f.nbJourneesOuvertes > 0 ? 'Une journée n\'est pas encore clôturée par le chef' : undefined}
            >Valider la semaine</button>
          )}
          {f.statut === 'validee' && regle === 0n && (
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={onRouvrir}>Annuler la validation</button>
          )}
          {f.statut === 'validee' && reste > 0n && (
            <button className="ds-btn ds-btn-p ds-btn-sm" onClick={onPayer}><Wallet size={14} /> Payer</button>
          )}
        </div>
      </div>

      {f.nbJourneesOuvertes > 0 && (
        <div className="ds-alert-strip ds-alert-w" style={{ margin: '0 16px 10px' }}>
          <span className="ds-alert-ico"><AlertTriangle size={16} /></span>
          <div className="ds-alert-txt" style={{ fontSize: 12 }}>
            {f.nbJourneesOuvertes} journée{f.nbJourneesOuvertes > 1 ? 's' : ''} non clôturée{f.nbJourneesOuvertes > 1 ? 's' : ''} :
            le chef doit saisir la production du soir avant que la semaine puisse être validée.
          </div>
        </div>
      )}

      {/* Les sept jours d'un coup d'œil. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6, padding: '0 16px 12px' }}>
        {Array.from({ length: 7 }, (_, i) => {
          const iso = new Date(Date.parse(`${lundi}T00:00:00Z`) + i * 86_400_000).toISOString().slice(0, 10);
          const j = parJour.get(iso);
          return (
            <div key={iso} style={{
              border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8, padding: '6px 4px', textAlign: 'center',
              background: !j ? 'transparent' : j.statut === 'ouverte' ? 'var(--warning-bg, #FEF3C7)' : 'var(--bg-2, #F9FAFB)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{JOURS[new Date(`${iso}T00:00:00Z`).getUTCDay()]} {Number(iso.slice(8))}</div>
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                {!j ? '—' : j.statut === 'ouverte' ? 'ouverte' : formatFCFA(j.montantCents).replace(/\s?FCFA$/, '')}
              </div>
              {j && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{j.presences.length} prés.</div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 0 }}>
        <table className="ds-tbl">
          <thead><tr><th>Produit dans la semaine</th><th style={{ width: 90 }}>Quantité</th><th style={{ width: 130 }}>Montant</th></tr></thead>
          <tbody>
            {f.produits.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--tx-3)', fontSize: 12 }}>Aucune production (journaliers seulement, ou journées non clôturées).</td></tr>}
            {f.produits.map((p) => (
              <tr key={p.produitId}>
                <td><strong>{p.code}</strong> <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>{p.libelle}</span></td>
                <td className="ds-am" style={{ fontWeight: 700 }}>{p.quantite.toLocaleString('fr-FR')}</td>
                <td className="ds-am">{formatFCFA(p.montantCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="ds-tbl">
          <thead><tr><th>Ouvrier</th><th style={{ width: 90 }}>Jours</th><th style={{ width: 130 }}>À payer</th><th style={{ width: 70 }}></th></tr></thead>
          <tbody>
            {f.ouvriers.map((o) => (
              <tr key={o.ouvrierId}>
                <td>
                  <div style={{ fontWeight: 600 }}>{o.ouvrier.nom}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                    {o.joursEquipe > 0 && `${o.joursEquipe} j équipe`}
                    {o.joursEquipe > 0 && o.joursJournalier > 0 && ' · '}
                    {o.joursJournalier > 0 && `${o.joursJournalier} j journalier`}
                  </div>
                </td>
                <td className="ds-am">{o.jours.toLocaleString('fr-FR')}</td>
                <td className="ds-am" style={{ fontWeight: 700 }}>{formatFCFA(o.montantCents)}</td>
                <td>{o.regleCents && <CheckCircle2 size={16} style={{ color: 'var(--success)' }} aria-label="Payé" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Paie d'une semaine : un passage en caisse, une écriture, une liste émargée.
 * Un ouvrier absent le jour de paie se décoche et se règle plus tard.
 */
function ModalePaie({ f, onFermer, onPaye }: { f: FeuilleSemaine; onFermer: () => void; onPaye: () => void }) {
  const aRegler = useMemo(() => f.ouvriers.filter((o) => !o.regleCents && BigInt(o.montantCents) > 0n), [f]);
  const [choisis, setChoisis] = useState<Set<string>>(() => new Set(aRegler.map((o) => o.ouvrierId)));
  const [mode, setMode]       = useState<ModePaiement>('especes');
  const [reference, setRef]   = useState('');
  const [preuve, setPreuve]   = useState<string | null>(null);
  const [preuveNom, setPreuveNom] = useState<{ nom: string; pdf: boolean } | null>(null);
  const [depot, setDepot]     = useState(false);

  const total = aRegler.filter((o) => choisis.has(o.ouvrierId)).reduce((s, o) => s + BigInt(o.montantCents), 0n);
  const preuveRequise = mode === 'especes';

  const payer = useMutation({
    mutationFn: () => mainOeuvreApi.payerSemaine(f.feuilleId, {
      mode, reference: reference.trim() || undefined, preuveUrl: preuve ?? undefined, ouvrierIds: [...choisis],
    }),
    onSuccess: (r) => { toast.success('Semaine payée', `${r.nbOuvriers} ouvrier(s) · écriture ${r.ecriture}.`); onPaye(); },
    onError:   (e: any) => toast.error('Paiement refusé', e?.response?.data?.error?.message ?? 'Opération impossible.'),
  });

  async function joindre(fichier: File) {
    setDepot(true);
    try {
      // 10/10/2026 : une image est réduite avant l'envoi ; un PDF (reçu, bordereau, liste scannée) part tel quel.
      const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name);
      if (!pdf && !fichier.type.startsWith('image/')) {
        toast.error('Fichier refusé', 'Joignez une photo (JPG, PNG) ou un PDF.');
        return;
      }
      if (pdf && fichier.size > PDF_MAX_OCTETS) {
        toast.error('PDF trop lourd', `${Math.round(fichier.size / 1024)} Ko — maximum ${Math.round(PDF_MAX_OCTETS / 1024)} Ko. Photographiez plutôt la pièce.`);
        return;
      }
      const { dataBase64, contentType } = pdf
        ? { dataBase64: await lireEnBase64(fichier), contentType: 'application/pdf' }
        : await reduirePhoto(fichier);
      setPreuve((await mainOeuvreApi.deposerPreuve({ dataBase64, contentType, nomOrigine: fichier.name })).url);
      setPreuveNom({ nom: fichier.name, pdf });
    } catch (e: any) {
      toast.error('Pièce non déposée', e?.response?.data?.error?.message ?? 'Réessayez.');
    } finally { setDepot(false); }
  }

  return (
    <div role="dialog" aria-modal="true" onClick={onFermer} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-0, #fff)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.25)',
      }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wallet size={18} /> Payer la semaine — {f.equipes.map((e) => e.nom).join(' + ') || 'Journaliers'}
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--tx-3)' }}>
          Saisi par {f.chefs.map((c) => c.nom).join(', ')} · décochez un ouvrier absent, il sera réglé plus tard.
        </p>

        <div style={{ border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8, marginBottom: 14 }}>
          {aRegler.map((o) => (
            <label key={o.ouvrierId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--bd-1, #E5E7EB)', fontSize: 13 }}>
              <input
                type="checkbox" checked={choisis.has(o.ouvrierId)}
                onChange={(e) => {
                  const s = new Set(choisis);
                  if (e.target.checked) s.add(o.ouvrierId); else s.delete(o.ouvrierId);
                  setChoisis(s);
                }}
              />
              <span style={{ flexGrow: 1 }}>{o.ouvrier.nom} <span style={{ color: 'var(--tx-3)', fontSize: 11 }}>{o.ouvrier.telephone}</span></span>
              <strong>{formatFCFA(o.montantCents)}</strong>
            </label>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', fontWeight: 800 }}>
            <span>Total remis</span><span>{formatFCFA(total)}</span>
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Mode de règlement</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {(Object.keys(LIBELLE_MODE) as ModePaiement[]).map((m) => (
            <button key={m} onClick={() => setMode(m)} className={`ds-btn ds-btn-sm ${mode === m ? 'ds-btn-p' : 'ds-btn-g'}`}>{LIBELLE_MODE[m]}</button>
          ))}
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
          {mode === 'especes' ? "Numéro de la liste d'émargement" : 'Référence de la transaction'}
        </label>
        <input
          value={reference} onChange={(e) => setRef(e.target.value)}
          placeholder={mode === 'especes' ? 'EMARG-2026-014' : 'MP260929.1234.A56789'}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 13 }}
        />

        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Pièce justificative — liste émargée, reçu{preuveRequise && <span style={{ color: 'var(--danger)' }}> — obligatoire</span>}
          </label>
          {preuve ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {preuveNom?.pdf ? (
                <a href={preuve} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>
                  PDF joint : {preuveNom.nom}
                </a>
              ) : (
                <img src={preuve} alt="Pièce justificative" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8 }} />
              )}
              <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => { setPreuve(null); setPreuveNom(null); }}>Retirer</button>
            </div>
          ) : (
            <>
              <input type="file" accept="image/*,application/pdf" disabled={depot}
                onChange={(e) => { const fi = e.target.files?.[0]; e.target.value = ''; if (fi) void joindre(fi); }}
                style={{ fontSize: 13, maxWidth: '100%' }} />
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--tx-3)', lineHeight: 1.45 }}>
                {depot ? 'Dépôt en cours…' : 'Photo ou fichier (image, PDF). '}{depot ? '' : preuveRequise
                  ? "Les espèces ne laissent aucune trace chez un tiers : la liste signée par chaque ouvrier est la seule preuve."
                  : "Facultatif — la référence fait déjà foi chez l'opérateur."}
              </p>
            </>
          )}
        </div>

        <div className="ds-alert-strip ds-alert-w" style={{ margin: '14px 0' }}>
          <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
          <div className="ds-alert-txt" style={{ fontSize: 12 }}>
            Une seule écriture <strong>641000 / {mode === 'especes' ? '571000' : '521000'}</strong>, une ligne par ouvrier.
            Un ouvrier payé ne peut pas l'être deux fois sur la même semaine.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onFermer}>Annuler</button>
          <button
            className="ds-btn ds-btn-p"
            disabled={payer.isPending || depot || choisis.size === 0 || (preuveRequise && !preuve)}
            onClick={() => payer.mutate()}
          >{payer.isPending ? 'Enregistrement…' : `Confirmer — ${formatFCFA(total)}`}</button>
        </div>
      </div>
    </div>
  );
}
